import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { auth, db, ref, set } from "../Firebase/firebase-config";
import { createUserWithEmailAndPassword, fetchSignInMethodsForEmail } from "firebase/auth";
import { get, onValue, remove, push, runTransaction } from "firebase/database";
import { useAccount } from "../context/AccountContext";
import { FiArrowLeft, FiCheckCircle, FiAlertCircle, FiLoader, FiTrash2 } from "react-icons/fi";
import { FaInfoCircle } from "react-icons/fa";
import "./AddFirms.css";

export default function AddFirm() {
  const { currentAccount, reload } = useAccount();
  const navigate = useNavigate();
const MAX_FIRM_REQUESTS_PER_DAY = 5;
const [remainingRequests, setRemainingRequests] = useState(MAX_FIRM_REQUESTS_PER_DAY);
  const [form, setForm] = useState({
    username: "", name: "", phone: "", email: "", gstin: "", pan: "",
  });
  const [firms, setFirms] = useState([]);
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState("");
  const [msgType, setMsgType] = useState(""); // success | error | warning
  const [showInfo, setShowInfo] = useState(false);

  // NEW - Partner-style states
  const [myCompanyName, setMyCompanyName] = useState("");
  const [pendingFirmRequestKey, setPendingFirmRequestKey] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null);
  const [activeMenu, setActiveMenu] = useState(null); // tracks which firm's menu is open

  const gifUrl = "/Images/New notification.gif";

  // ==================== FETCH MY COMPANY NAME ====================
  useEffect(() => {
    if (!currentAccount?.uid) return;
    const profileRef = ref(db, `profileDashboard/${currentAccount.uid}`);
    const unsub = onValue(profileRef, (snap) => {
      const data = snap.val() || {};
      setMyCompanyName(data.company_name || data.name || currentAccount.name || "Your Company");
    });
    return () => unsub();
  }, [currentAccount]);

  useEffect(() => {
  if (!currentAccount?.uid) return;

  const today = getTodayDateKey();
  const countRef = ref(db, `limit_counter/daily_firm_requests/${currentAccount.uid}/${today}`);

  const unsub = onValue(countRef, (snap) => {
    const count = snap.val() || 0;
    setRemainingRequests(Math.max(0,  MAX_FIRM_REQUESTS_PER_DAY - count));
  });

  return () => unsub();
}, [currentAccount]);

  // ==================== EXISTING FIRMS (unchanged) ====================
  useEffect(() => {
    if (currentAccount?.type !== "main") return;
    const firmsRef = ref(db, `profileDashboard/${currentAccount.uid}/firms`);
    const unsubscribe = onValue(firmsRef, async (snap) => {
      const data = snap.val() || {};
      const firmUids = Object.keys(data);
      const firmPromises = firmUids.map(async (uid) => {
        const profileSnap = await get(ref(db, `profileDashboard/${uid}`));
        const profile = profileSnap.val();
        if (!profile) return null;
        return { uid, pwd: data[uid]?.pwd || "", ...profile };
      });
      const fullFirms = (await Promise.all(firmPromises)).filter(Boolean);
      setFirms(fullFirms);
    });
    return () => unsubscribe();
  }, [currentAccount]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (activeMenu && !event.target.closest('.firm-actions-menu')) {
        setActiveMenu(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [activeMenu]);

  // ==================== LIVE PENDING REQUEST TRACKER ====================
  useEffect(() => {
    if (!pendingFirmRequestKey) return;

    const reqRef = ref(db, `firmRequests/${pendingFirmRequestKey}`);
    const unsub = onValue(reqRef, (snap) => {
      const req = snap.val();
      if (!req) {
        setPendingFirmRequestKey(null);
        return;
      }
      if (req.status === "approved") {
        setMsg("✅ Firm request APPROVED! New firm will appear shortly.");
        setMsgType("success");
        setPendingFirmRequestKey(null);
        reload();
      } else if (req.status === "rejected") {
        setMsg(`❌ Firm request REJECTED${req.rejectReason ? ` • ${req.rejectReason}` : ""}`);
        setMsgType("error");
        setPendingFirmRequestKey(null);
      }
    });

    return () => unsub();
  }, [pendingFirmRequestKey, reload]);

  // ==================== HELPERS ====================
  const getIndianTimestamp = () => {
    const now = new Date();
    const istOffset = 5.5 * 60 * 60 * 1000;
    const istTime = new Date(now.getTime() + istOffset);
    return istTime.toISOString().replace("T", " ").replace("Z", "");
  };

  const sendFirmRequestNotificationCentralized = async (requestKey, requestedByUid, companyName, firmName, firmEmail) => {
    const notificationRef = ref(db, `supplier_notifications/firm_requests/${requestedByUid}/${requestKey}`);
    const message = `${companyName} has requested a new firm account • Firm: ${firmName} • Email: ${firmEmail}`;

    const newNotification = {
      title: "New Firm Creation Request",
      message,
      timestamp: getIndianTimestamp(),
      gifUrl,
      seen: false,
    };

    try {
      await set(notificationRef, newNotification);
    } catch (error) {
      console.error("Notification error:", error);
    }
  };

  const showMessage = (text, type = "success") => {
    setMsg(text);
    setMsgType(type);
    setTimeout(() => { setMsg(""); setMsgType(""); }, 7000);
  };

  // ==================== AUTO PAN ====================
  const handleGST = (gstValue) => {
    const cleanGST = gstValue.toUpperCase().trim();
    if (cleanGST.length >= 12) {
      const autoPAN = cleanGST.substring(2, 12);
      setForm({ ...form, gstin: cleanGST, pan: autoPAN });
    } else {
      setForm({ ...form, gstin: cleanGST, pan: "" });
    }
  };

  const getTodayDateKey = () => {
  const now = new Date();
  const istOffset = 5.5 * 60 * 60 * 1000;
  const istDate = new Date(now.getTime() + istOffset);
  istDate.setHours(0, 0, 0, 0);
  return istDate.toISOString().split("T")[0];
};
  // ==================== SEND REQUEST (exact partner logic) ====================
  const handleSendRequest = async (e) => {
    e.preventDefault();
    if (currentAccount?.type !== "main") {
      showMessage("Only the main account can create firms.", "error");
      return;
    }

    setMsg("");
    setLoading(true);
    const today = getTodayDateKey();
const countRef = ref(db, `limit_counter/daily_firm_requests/${currentAccount.uid}/${today}`);

const snap = await get(countRef);
const currentCount = snap.val() || 0;

if (currentCount >=  MAX_FIRM_REQUESTS_PER_DAY) {
  showMessage("Daily limit (5 firm requests) reached. Try again tomorrow.", "error");
  return;
}

    try {
      // ────── All your original validations ──────
      const cleanPhone = form.phone.replace(/[^\d]/g, "");
      if (cleanPhone.length !== 10) throw new Error("Enter a valid 10-digit phone number");

      // Username uniqueness
      const usernameSnap = await get(ref(db, `profileDashboard`));
      const existingUsernames = Object.values(usernameSnap.val() || {})
        .filter(u => u?.username)
        .map(u => u.username.trim().toLowerCase());
      if (existingUsernames.includes(form.username.trim().toLowerCase())) {
        throw new Error("This username is already taken.");
      }

      // Real email
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) throw new Error("Valid email required");
      const emailMethods = await fetchSignInMethodsForEmail(auth, form.email);
      if (emailMethods.length > 0) throw new Error("This email is already in use.");

      // Dummy phone email
      const dummyEmailCheck = `${cleanPhone}@firm.altrange.internal`;
      const dummyMethods = await fetchSignInMethodsForEmail(auth, dummyEmailCheck);
      if (dummyMethods.length > 0) throw new Error("This phone number is already linked to a firm.");

      // Show confirmation modal (exactly like partner)
      setConfirmAction({
        type: "confirmSendFirm",
        data: {
          firmName: form.name.trim(),
          email: form.email.trim(),
          phone: form.phone.trim(),
          gstin: form.gstin || "N/A",
        },
        onConfirm: performSendFirmRequest,
      });

    } catch (err) {
      showMessage(err.message || "Validation failed", "error");
    } finally {
      setLoading(false);
    }
  };

  const performSendFirmRequest = async () => {
    setLoading(true);
    
    try {
      const cleanPhone = form.phone.replace(/[^\d]/g, "");
      const dummyEmail = `${cleanPhone}@firm.altrange.internal`;
      const dummyPwd = Math.random().toString(36).slice(-8) + "Xy1!";

      const newRequestRef = push(ref(db, 'firmRequests'));
      // ✅ DAILY LIMIT CHECK (ADD HERE)
const today = getTodayDateKey();
const countRef = ref(db, `limit_counter/daily_firm_requests/${currentAccount.uid}/${today}`);

await runTransaction(countRef, (current) => {
  const count = current || 0;

  if (count >=  MAX_FIRM_REQUESTS_PER_DAY) {
    throw new Error("daily_limit_reached");
  }

  return count + 1;
});
      await set(newRequestRef, {
        requestedBy: currentAccount.uid,
        username: form.username.trim(),
        company_name: form.name.trim(),
        phone: form.phone.trim(),
        email: form.email.trim(),
        gstin: form.gstin || null,
        pan: form.pan || null,
        dummyEmail,
        dummyPwd,           // admin will use this to create auth user
        status: "pending",
        requestedAt: Date.now(),
      });

      const requestKey = newRequestRef.key;

      await sendFirmRequestNotificationCentralized(
        requestKey,
        currentAccount.uid,
        myCompanyName,
        form.name.trim(),
        form.email.trim()
      );

      setPendingFirmRequestKey(requestKey);
      showMessage("✅ Request sent to admin for approval!", "success");

      // Reset form
      setForm({ username: "", name: "", phone: "", email: "", gstin: "", pan: "" });
      setConfirmAction(null);
      setLoading(false);

   } catch (err) {
  if (err.message === "daily_limit_reached") {
    showMessage("Daily limit (5 firm requests) reached.", "error");
  } else {
    showMessage("Failed to send request: " + err.message, "error");
  }
  setLoading(false);
}
  };

  const handleDelete = async (firm) => {
    if (!window.confirm(`Permanently delete "${firm.company_name}"?\n\nThis will delete ALL data (files, chats, orders, etc.) and the account.\nThis action CANNOT be undone.`)) {
      return;
    }

    setLoading(true);
    setMsg("");
    setMsgType("");

    try {
      const firmUid = firm.uid;

      // ────────────────────────────────────────────────
      await set(ref(db, `profileDashboard/${firmUid}/deleteRequested`), {
        requestedBy: currentAccount.uid,
        at: new Date().toISOString(),
        reason: "Manual delete by parent account",
      });

      // 2. Delete main profile & firm link under parent
      await remove(ref(db, `profileDashboard/${firmUid}`));
      await remove(ref(db, `profileDashboard/${currentAccount.uid}/firms/${firmUid}`));

      // 3. Delete all known per-firm data paths
      const dbPathsToDelete = [
        `issues/${firmUid}`,
        `machineryOrder/${firmUid}`,
        `needHelp/${firmUid}`,
        `Finance/${firmUid}`,
        `negotiations/${firmUid}`,
        `new-code/${firmUid}`,
        `newProducts/${firmUid}`,
        `notes/${firmUid}`,
        `offers/${firmUid}`,
        `orders/${firmUid}`,
        `priceInformation/${firmUid}`,
        `queries/${firmUid}`,
        `quoteRequests/${firmUid}`,
        `rates/${firmUid}`,
        `rateRequest/${firmUid}`,
        `refunds/${firmUid}`,
        `regions/${firmUid}`,
        `steelOrder/${firmUid}`,
        `supplierContacts/${firmUid}`,
        `supplier_notifications/${firmUid}`,
        `transport_goods/${firmUid}`,
        `transport_goods_replies/${firmUid}`,
        `trendingProducts/${firmUid}`,
        `userBrandAlternatives/${firmUid}`,
        `userComparisonData/${firmUid}`,
        `user_notifications/${firmUid}`,
        `userchatbox/${firmUid}`,
        `groupChats/${firmUid}`,
        `ActivePO/${firmUid}`,
        `accessPermissions/${firmUid}`,
        `alt_alarm/${firmUid}`,
        `broadcasts/${firmUid}`,
        `feedback/${firmUid}`,
        `loanRequest/${firmUid}`,
        // Add any other firm-specific paths you have
      ];

      await Promise.all(
        dbPathsToDelete.map(path =>
          remove(ref(db, path)).catch(err => {
            console.warn(`Failed to delete ${path}:`, err.message);
            return null; // continue even if some paths don't exist
          })
        )
      );

      // ────────────────────────────────────────────────
      // 4. Delete Storage folders (very important!)
      // ────────────────────────────────────────────────
      const { getStorage, ref: storageRef, listAll, deleteObject } = await import("firebase/storage");
      const storage = getStorage();

      const storageFolders = [
        `Finance/${firmUid}`,
        `POFiles/${firmUid}`,
        `PaymentFiles/${firmUid}`,
        `chatDocuments/${firmUid}`,
        `chatFiles/${firmUid}`,
        `mtc/${firmUid}`,
        `pendingProducts/${firmUid}`,
        `refunds/${firmUid}`,
        `user-statements/${firmUid}`,
        `userDocuments/${firmUid}`,
        // Add more if needed, e.g. `profilePictures/${firmUid}`
      ];

      await Promise.all(
        storageFolders.map(async (folderPath) => {
          try {
            const folderRef = storageRef(storage, folderPath);
            const { items, prefixes } = await listAll(folderRef);

            // Delete files
            await Promise.all(items.map(item => deleteObject(item).catch(() => { })));

            // Recurse into subfolders (if any)
            await Promise.all(
              prefixes.map(prefix =>
                listAll(prefix).then(({ items }) =>
                  Promise.all(items.map(item => deleteObject(item).catch(() => { })))
                )
              )
            );
          } catch (err) {
            if (!err.code?.includes("not-found")) {
              console.warn(`Storage cleanup failed for ${folderPath}:`, err);
            }
          }
        })
      );

      // ────────────────────────────────────────────────
      // 5. Update local UI + reload context
      // ────────────────────────────────────────────────
      setFirms(prev => prev.filter(f => f.uid !== firmUid));
      setMsg("Firm and all associated data permanently deleted!");
      setMsgType("success");

      await reload(); // refresh account context if needed

    } catch (err) {
      console.error("Delete firm failed:", err);
      setMsg("Partial deletion occurred. Some data may remain. Contact support if needed.");
      setMsgType("error");
    } finally {
      setLoading(false);
    }
  };
  return (
    <div className="add-firm-page-wrapper">
      <div className="add-firm-card">
        {/* Header */}
        <div className="add-firm-header">
          <button onClick={() => navigate(-1)} className="back-btn"><FiArrowLeft size={20} /></button>
          <h1>Add New Firm</h1>
          <button onClick={() => setShowInfo(true)} className="info-btn"><FaInfoCircle size={20} /></button>
        </div>

        <div className="add-firm-card-content">
          {/* Existing Firms (unchanged) */}
          {currentAccount?.type === "main" && (
            <div className="existing-firms">
              <h2 className="text-center">Existing Firms</h2>

              {firms.length > 0 ? (
                <div className="firm-list">
                  {firms.map((firm) => (
                    <div key={firm.uid} className="firm-card">
                      {/* Firm Info */}
                      <div className="firm-card-header">
                        <h3>{firm.company_name}</h3>

                        {/* Three-dot menu */}
                        <div className="firm-actions-menu">
                          <button
                            className="three-dots-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              // Toggle menu for this specific card
                              setActiveMenu(activeMenu === firm.uid ? null : firm.uid);
                            }}
                          >
                            <span className="dots">•••</span>
                          </button>

                          {/* Dropdown menu - shows only when this card is active */}
                          {activeMenu === firm.uid && (
                            <div className="firm-menu-dropdown">
                              <button
                                className="menu-item delete-item"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDelete(firm);
                                  setActiveMenu(null); // close menu after action
                                }}
                              >
                                <FiTrash2 size={16} />
                                Delete Firm
                              </button>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Firm Details */}
                      <div className="firm-info">
                        <p><strong>Username:</strong> {firm.username}</p>
                        <p><strong>Phone:</strong> {firm.phone}</p>
                        <p><strong>Email:</strong> {firm.email || "N/A"}</p>
                        <p><strong>GST:</strong> {firm.gstin || "N/A"}</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="no-firms">No existing firms yet.</p>
              )}
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSendRequest} className="add-firm-form">
            <div className="form-grid">
              {/* All your inputs exactly same */}
              <div className="firm-group">
                <label>Username <span className="required">*</span></label>
                <input type="text" required value={form.username} onChange={e => setForm({ ...form, username: e.target.value })} placeholder="e.g. xyz_traders" />
              </div>
              <div className="firm-group">
                <label>Firm Name <span className="required">*</span></label>
                <input type="text" required value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="XYZ Traders Pvt Ltd" />
              </div>
              <div className="firm-group">
                <label>Phone Number <span className="required">*</span></label>
                <input type="tel" required value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} placeholder="+91 98765 43210" maxLength="15" />
              </div>
              <div className="firm-group">
                <label>Email <span className="required">*</span></label>
                <input type="email" required value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} placeholder="firm@example.com" />
              </div>
              <div className="firm-group">
                <label>GST Number (Optional)</label>
                <input type="text" value={form.gstin} onChange={e => handleGST(e.target.value)} placeholder="22AAAAA0000A1Z5" maxLength="15" style={{ textTransform: "uppercase" }} />
              </div>
              <div className=" firm-group">
                <label>PAN (Auto-filled)</label>
                <input type="text" value={form.pan} readOnly placeholder="Will be auto-filled from GST" className="readonly-input" />
              </div>
            </div>

            <div className="form-actions">
              <button
  type="submit"
  disabled={
    loading ||
    currentAccount?.type !== "main" ||
    remainingRequests <= 0
  }
  className="f-btn-primary"
>
  {loading ? (
    <>
      <FiLoader className="spin" /> Sending Request...
    </>
  ) : remainingRequests <= 0 ? (
    "Limit Reached"
  ) : (
    "Send Request"
  )}
</button>
              <button type="button" onClick={() => navigate(-1)} className="btn-secondary">Cancel</button>
              
            </div>
           
<p style={{ marginTop: "10px", color: remainingRequests <= 1 ? "red" : "" }} className="firm-limit-msg">
  Remaining requests today: {remainingRequests}/5
</p>
            {/* Pending Banner */}
            {pendingFirmRequestKey && (
              <div style={{ marginTop: "12px", padding: "14px", background: "#ecfdf5", border: "1px solid #a7f3d0", borderRadius: "8px", color: "#166534" }}>
                ⏳ <strong>Request sent • Awaiting Admin Approval</strong><br />
                <small>Your firm request is with the admin. Once approved, it will appear in the list automatically.</small>
              </div>
            )}

            {msg && (
              <div className={`alert alert-${msgType}`}>
                {msgType === "success" ? <FiCheckCircle /> : <FiAlertCircle />}
                <span>{msg}</span>
              </div>
            )}
          </form>
        </div>
      </div>

      {/* Info Modal (unchanged) */}
      {showInfo && (
        <div className="modal-overlay" onClick={() => setShowInfo(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <h2>About Add Firm (with Approval)</h2>
            <p>Now every new firm goes through admin approval. You will be notified instantly when approved/rejected.</p>
            <button onClick={() => setShowInfo(false)} className="btn-secondary">Close</button>
          </div>
        </div>
      )}

      {/* Confirm Modal - Exact same style as PartnerManagement */}
      {confirmAction && (
        <div className="modal-overlay" onClick={() => setConfirmAction(null)}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: "420px" }}>
            <h3>Confirm Firm Request</h3>
            <p>You are about to send a request to add a new firm:</p>
            <div style={{ background: "#f8fafc", padding: "12px", borderRadius: "6px", margin: "10px 0" }}>
              <strong>Firm:</strong> {confirmAction.data.firmName}<br />
              <strong>Email:</strong> {confirmAction.data.email}<br />
              <strong>Phone:</strong> {confirmAction.data.phone}<br />
              <strong>GST:</strong> {confirmAction.data.gstin}
            </div>
            <p style={{ color: "#c2410c" }}>This request will go to the admin for final approval.</p>

            <div className="pm-modal-actions" style={{ display: "flex", gap: "10px", marginTop: "20px" }}>
              <button onClick={() => setConfirmAction(null)} className="btn-secondary">Cancel</button>
              <button
                onClick={() => confirmAction.onConfirm()}
                className="f-btn-primary"
                disabled={loading}
              >
                {loading ? "Sending..." : "Yes, Send Request"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Info Modal - Updated for Approval Flow */}
      {showInfo && (
        <div className="modal-overlay" onClick={() => setShowInfo(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>About the Add Firm </h2>
            <p>
              This page allows you (as the main account holder) to create and manage sub-accounts called "Firms." Firms are linked to your main account and can perform tasks like managing orders, stocks, viewing and downloading documents etc... However, only the main account can create or delete firms.
            </p>
            <p>
              <strong>How to use:</strong>
              <br />
              - Fill in the form details to add a new firm. Username, Firm Name,Email, and Phone Number are required.
              <br />
              - GST is optional, and PAN will auto-fill from GST if provided.<br />
              • Click <strong>"Send Request to Admin for Approval"</strong>.<br />
              • Once approved, the new firm will automatically appear in your "Existing Firms" list. <br />
              - View and delete existing firms in the section above the form.
            </p>
            <p>
              <strong>Role of Firms:</strong> Firms act as independent entities under your control, ideal for managing multiple businesses or branches without full admin access.
            </p>
            <button onClick={() => setShowInfo(false)} className="btn-secondary">
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}